import type { PathPoint, PathSample, Track } from './types';

/*
  Профиль скорости курьера (spec-3-motion §6): один на весь путь, как у машины на трассе — мягкий разгон,
  торможение перед поворотом, долгий бережный подход. Своих кривых у отрезков нет: на каждом стыке была бы
  остановка и рывок. Чистые функции.

  Расчёт: сетка 2 px; проход вперёд с пределом разгона, назад — с пределами торможения перед поворотами и
  остановки у цели; минимум с пределами поворотов (боковое ускорение) и крейсерской; сглаживание скользящим
  средним по окну 48 мс; из v(s) — t(s). Короче 420 мс — все скорости ниже (в меню не мельтешит).
  Кадры WAAPI запекаются из t(s): шаг не больше 16 мс, в поворотах — не больше 8 px пути и 12° курса;
  между кадрами — linear.
*/

/** Темп — одно место (spec-3-motion §12). Скорости в px/мс, ускорения в px/мс². */
export const V_MIN = 0.9;
export const V_MAX = 1.5;
export const A_ACC = 0.01;
export const A_BRAKE = 0.01;
export const A_STOP = 0.006;
export const A_STOP_HOME = 0.008;
export const A_LAT = 0.06;
export const A_ABORT = 0.012;
export const SMOOTH_MS = 48;
export const ROUTE_MIN_MS = 420;
export const ROUTE_MAX_MS = 1150;
export const RETURN_V = 1.1;
/** Кадры: шаг по времени, по пути и по курсу в поворотах. */
export const KF_MS = 16;
export const KF_PX = 8;
export const KF_DEG = 12;
/** Сетка профиля, px. */
export const GRID = 2;

/** Крейсерская скорость: длинная дорога идёт чуть быстрее; обратно — ×1,1 (не выше потолка). */
export function cruise(length: number, back = false): number {
  const v = Math.min(V_MAX, Math.max(V_MIN, 0.8 + length / 1500));
  return back ? Math.min(V_MAX, v * RETURN_V) : v;
}

export interface ProfileOptions {
  /** Скорость в начале и в конце, px/мс. */
  v0?: number;
  v1?: number;
  /** Крейсерская. */
  vc: number;
  /** Остановка в конце (у цели — A_STOP, дома — A_STOP_HOME, торможение при отказе — A_ABORT). */
  aStop?: number;
  /** Короче — растянуть до этого времени. */
  minMs?: number;
}

const lerp = (a: number, b: number, f: number) => a + (b - a) * f;

/** Плотные точки пути → трасса: сетка 2 px, скорость, время. */
export function buildTrack(pts: PathPoint[], o: ProfileOptions): PinnedTrack {
  const v0 = o.v0 ?? 0;
  const v1 = o.v1 ?? 0;
  const aStop = o.aStop ?? A_STOP;
  // Длина вдоль плотных точек.
  const cum: number[] = [0];
  for (let i = 1; i < pts.length; i++)
    cum.push(cum[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y));
  const L = cum[cum.length - 1];
  const n = Math.max(1, Math.ceil(L / GRID));
  const ds = L / n;

  // Сетка: позиция и курс — интерполяцией, кривизна — наибольшая в ячейке (поворот не проскочит между узлами).
  const grid: { s: number; x: number; y: number; h: number; k: number; p: PathPoint }[] = [];
  let j = 0;
  for (let i = 0; i <= n; i++) {
    const s = Math.min(L, i * ds);
    while (j < pts.length - 2 && cum[j + 1] < s) j++;
    const seg = cum[j + 1] - cum[j];
    const f = seg > 0 ? Math.min(1, Math.max(0, (s - cum[j]) / seg)) : 0;
    const a = pts[j];
    const b = pts[Math.min(j + 1, pts.length - 1)];
    grid.push({
      s,
      x: lerp(a.x, b.x, f),
      y: lerp(a.y, b.y, f),
      h: lerp(a.h, b.h, f),
      k: 0,
      p: f < 0.5 ? a : b,
    });
  }
  let q0 = 0;
  for (let i = 0; i <= n; i++) {
    const lo = grid[Math.max(0, i - 1)].s;
    const hi = grid[Math.min(n, i + 1)].s;
    while (q0 < pts.length - 1 && cum[q0] < lo) q0++;
    let k = 0;
    for (let q = Math.max(0, q0 - 1); q < pts.length && cum[q] <= hi; q++)
      if (Math.abs(pts[q].k) > Math.abs(k)) k = pts[q].k;
    grid[i].k = k;
  }

  // Пределы: крейсерская, потолок, боковое ускорение в повороте.
  const lim = grid.map((g) => Math.min(o.vc, V_MAX, g.k !== 0 ? Math.sqrt(A_LAT / Math.abs(g.k)) : Infinity));
  const v = new Array<number>(n + 1);
  v[0] = v0;
  for (let i = 1; i <= n; i++) v[i] = Math.min(lim[i], Math.sqrt(v[i - 1] * v[i - 1] + 2 * A_ACC * ds));
  v[n] = Math.min(v[n], v1);
  for (let i = n - 1; i >= 1; i--) {
    v[i] = Math.min(
      v[i],
      Math.sqrt(v[i + 1] * v[i + 1] + 2 * A_BRAKE * ds),
      Math.sqrt(v1 * v1 + 2 * aStop * (L - grid[i].s)),
    );
  }
  v[n] = v1;

  // Сглаживание: окно 48 мс в длине — 48·v px; у концов окно сжимается, края остаются на месте.
  const pref: number[] = [0];
  for (let i = 0; i <= n; i++) pref.push(pref[i] + v[i]);
  const sm = v.map((vi, i) => {
    if (i === 0 || i === n) return vi;
    const half = Math.min(Math.round(((SMOOTH_MS / 2) * vi) / ds), i, n - i);
    if (half < 1) return vi;
    return (pref[i + half + 1] - pref[i - half]) / (2 * half + 1);
  });

  // Время из скорости; трапеции, у остановки — равноускоренно.
  const t: number[] = [0];
  for (let i = 1; i <= n; i++) {
    const vs = sm[i - 1] + sm[i];
    t.push(t[i - 1] + (vs > 1e-6 ? (2 * ds) / vs : Math.sqrt((2 * ds) / A_ACC)));
  }
  const T = t[n];
  const scale = o.minMs && T < o.minMs && T > 0 ? T / o.minMs : 1;
  const samples: PathSample[] = grid.map((g, i) => ({
    t: t[i] / scale,
    x: g.x,
    y: g.y,
    heading: g.h,
    speed: sm[i] * scale,
    accel: 0,
    curvature: g.k,
    lane: g.p.lane,
    normal: g.p.normal,
    room: g.p.room,
  }));
  for (let i = 0; i <= n; i++) {
    const a = samples[Math.max(0, i - 1)];
    const b = samples[Math.min(n, i + 1)];
    samples[i].accel = b.t > a.t ? (b.speed - a.speed) / (b.t - a.t) : 0;
  }
  return trackOf(
    samples,
    L,
    grid.map((g) => g.p.pin),
  );
}

/** Трасса с привязкой к рамке по точкам сетки (для слоя attach) — в той же сетке, что samples. */
export interface PinnedTrack extends Track {
  pins: number[];
}

export function trackOf(samples: PathSample[], length: number, pinList: number[]): PinnedTrack {
  const duration = samples.length ? samples[samples.length - 1].t : 0;
  const at = (t: number): PathSample => {
    if (t <= 0 || samples.length < 2) return samples[0];
    if (t >= duration) return samples[samples.length - 1];
    let lo = 0;
    let hi = samples.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (samples[mid].t <= t) lo = mid;
      else hi = mid;
    }
    const a = samples[lo];
    const b = samples[hi];
    const f = b.t > a.t ? (t - a.t) / (b.t - a.t) : 0;
    return {
      ...a,
      t,
      x: lerp(a.x, b.x, f),
      y: lerp(a.y, b.y, f),
      heading: lerp(a.heading, b.heading, f),
      speed: lerp(a.speed, b.speed, f),
      accel: lerp(a.accel, b.accel, f),
    };
  };
  return { samples, duration, length, at, pins: pinList };
}

/** Индекс точки сетки на момент t. */
export function indexAt(track: Track, t: number): number {
  const s = track.samples;
  if (t <= 0) return 0;
  if (t >= track.duration) return s.length - 1;
  let lo = 0;
  let hi = s.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (s[mid].t <= t) lo = mid;
    else hi = mid;
  }
  return lo;
}

/** Ключевой кадр корня: момент и позиция. */
export interface KeyPoint {
  t: number;
  x: number;
  y: number;
  heading: number;
  speed: number;
}

/** Кадры WAAPI из трассы: шаг ≤ 16 мс; в повороте ≤ 8 px пути и ≤ 12° курса. */
export function bakeKeys(track: Track): KeyPoint[] {
  const s = track.samples;
  if (s.length === 0) return [];
  const keys: KeyPoint[] = [];
  const push = (p: PathSample) => keys.push({ t: p.t, x: p.x, y: p.y, heading: p.heading, speed: p.speed });
  push(s[0]);
  let last = 0;
  const deg = (KF_DEG * Math.PI) / 180;
  for (let i = 1; i < s.length; i++) {
    const a = s[last];
    const b = s[i];
    let turning = false;
    let path = 0;
    for (let q = last + 1; q <= i; q++) {
      if (Math.abs(s[q].curvature) > 1e-4) turning = true;
      path += Math.hypot(s[q].x - s[q - 1].x, s[q].y - s[q - 1].y);
    }
    const over = b.t - a.t > KF_MS || (turning && (path > KF_PX || Math.abs(b.heading - a.heading) > deg));
    if (over && i - 1 > last) {
      push(s[i - 1]);
      last = i - 1;
      i--;
    } else if (over) {
      push(b);
      last = i;
    }
  }
  if (last !== s.length - 1) push(s[s.length - 1]);
  return keys;
}

/** Кадры корня для WAAPI: translate по кадрам, между ними linear. */
export function rootFrames(keys: KeyPoint[], duration: number): Keyframe[] {
  return keys.map((k) => ({
    offset: duration > 0 ? Math.min(1, k.t / duration) : 0,
    transform: `translate(${k.x.toFixed(2)}px, ${k.y.toFixed(2)}px)`,
  }));
}

/** Наибольшее отклонение хорды между соседними кадрами от точек пути, px (тест: не больше 0,5). */
export function chordError(track: Track, keys: KeyPoint[]): number {
  let worst = 0;
  const s = track.samples;
  let q = 0;
  for (let i = 1; i < keys.length; i++) {
    const a = keys[i - 1];
    const b = keys[i];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    while (q < s.length && s[q].t < b.t) {
      if (s[q].t > a.t && len > 1e-6) {
        const d = Math.abs((s[q].x - a.x) * dy - (s[q].y - a.y) * dx) / len;
        if (d > worst) worst = d;
      }
      q++;
    }
  }
  return worst;
}

/**
 * Торможение по той же дороге до остановки: с ускорением a от точки сетки i. Трасса — кусок пути вперёд
 * (форма плещет вперёд, тело видит accel < 0); если дорога кончится раньше — встаёт в её конце.
 */
export function brakeTrack(track: PinnedTrack, i: number, a: number): PinnedTrack {
  const s = track.samples;
  const v0 = s[i].speed;
  const d = (v0 * v0) / (2 * a);
  const out: PathSample[] = [];
  const pinList: number[] = [];
  let dist = 0;
  let t = 0;
  for (let q = i; q < s.length; q++) {
    if (q > i) dist += Math.hypot(s[q].x - s[q - 1].x, s[q].y - s[q - 1].y);
    const v = Math.sqrt(Math.max(0, v0 * v0 - 2 * a * dist));
    if (q > i) {
      const prev = out[out.length - 1];
      const step = Math.hypot(s[q].x - prev.x, s[q].y - prev.y);
      t += prev.speed + v > 1e-6 ? (2 * step) / (prev.speed + v) : 0;
    }
    out.push({ ...s[q], t, speed: v, accel: -a });
    pinList.push(track.pins[q]);
    if (dist >= d || v <= 1e-3) break;
  }
  out[out.length - 1].speed = 0;
  return trackOf(out, dist, pinList);
}
